import React, {
  FC,
  MouseEventHandler,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useAppSelector } from 'hooks/redux';
import { getText } from 'utils/stringUtil';
import {
  PR_BASED_ON_COVERING_BENEFITS,
  PR_EDIT_RESPONSES,
  PR_MORE_PLANS,
  PR_DOWNLOAD_RESULTS,
  PR_COVERAGE_JUST_YOU,
  PR_COVERAGE_YOU_PARTNER,
  PR_COVERAGE_YOU_CHILDREN,
  PR_COVERAGE_YOU_FAMILY,
  PR_NOT_UNDERLINED,
  PR_VISITING_THE_DOCTOR,
  PR_RARELY,
  PR_OCCASIONALLY,
  PR_OFTEN,
  PR_FREQUENTLY,
  PR_NOT_UNDERLINED_SPECIALIST,
  PR_VISITING_A_SPECIALIST,
  PR_NOT_UNDERLINED_PROCEDURES,
  PR_PLANNING_A,
  PR_PREGNANCY_OR_PROCEDURES,
  PR_PLANNING_PREGNANCY_PROCEDURES,
  PR_USING_A,
  PR_NARROW_NETWORK,
  PR_WIDE_NETWORK,
  PR_KAISER,
  PR_NOT_TAKING_PRESCRIPTIONS,
  PR_USING_COST_DRUGS,
  PR_AND_USING,
  PR_WE_RECOMMEND,
  PR_PLAN,
  PR_PLANS,
  PR_FOR_YOU,
  PR_NO_RECOMMENDATIONS,
  PR_PREMIUM,
  PR_PLAN_NETWORK,
  PR_DEDUCTIBLE,
  PR_OUT_OF_POCKET_MAX,
  PR_OFFICE_VISIT,
  PR_SPECIALIST_VISIT,
  HOSPITAL_INPATIENT,
  HOSPITAL_OUTPATIENT,
  PR_RETAIL_TIER,
  PR_HSA_CONTRIBUTION_HRA_ALLOWANCE,
  PR_EST_ANNUAL_COST,
  PR_EST_ANNUAL_COST_BREAKDOWN,
  PR_UNABLE_TO_RECOMMEND,
  PR_EDIT_REFINE_RESPONSES,
} from 'constants/languageConstants';

import useBenguide from 'hooks/benguide';
import AnnualCostBreakdown from 'modules/planRecommender/components/AnnualCostBreakdown/AnnualCostBreakdown';
import PlanRecommenderPlan from 'modules/planRecommender/components/PlanRecommenderPlan/PlanRecommenderPlan';
import {
  getFrequencyName,
  PLAN_RECOMMENDER_QUESTIONS,
} from 'modules/planRecommender/pages/constants';
import { getCurrentAnswerByQuestionType } from 'modules/planRecommender/pages/PlanRecommender/PlanRecommender';
import PlanRecommenderDisclaimer from 'modules/planRecommender/components/PlanRecommenderDisclaimer/PlanRecommenderDisclaimer';

import {
  TableContext,
  useTableHeightSync,
} from 'modules/benefitsWellness/components/TableContext/TableContextProvider';
import styles from './planRecommenderPlans.module.less';

type PlanRecommenderPlansProps = {
  plans: any;
  onEdit: MouseEventHandler<HTMLSpanElement>;
  benAdminUrl: string;
  downloadResultPdf: MouseEventHandler<HTMLButtonElement>;
  questions: any;
  answersInProgress: boolean;
  toggleMorePlansInfo: any;
  isEditDrawerOpen: boolean;
  resultInProgress: boolean;
};

const PlanRecommenderPlans: FC<PlanRecommenderPlansProps> = (
  props: PlanRecommenderPlansProps
) => {
  const { getMaxHeight, reset: resetTableContext } = useContext(TableContext);
  const { height: hsaHeight, ref: hsaRef } = useTableHeightSync('hsa', 1000);
  const [activePlan, setActivePlan] = useState<number>(0);
  const [isMultipleRecommendations, setIsMultipleRecommendations] =
    useState<boolean>(false);
  const [questions] = useState(props.questions);

  const benguide = useBenguide();
  const { benefitClasses } = benguide;
  const { plans } = useAppSelector((state) => state.planRecommender);
  const language = useAppSelector((state) => state.benguide.data?.language);

  useEffect(() => {
    const recommendedPlans = plans.filter((plan: any) => plan?.rank === 1);
    setIsMultipleRecommendations(recommendedPlans.length > 1);
  }, [plans]);

  // Transforms rates object to extract rates for first benefit class of the benguide
  const transformedPlans = useMemo(() => {
    resetTableContext();
    if (plans.length > 0 && benefitClasses) {
      return plans.map((plan) => {
        const clonedPlan = cloneDeep(plan);
        let selectedRate = null;
        for (const group in plan?.rates) {
          if (benefitClasses?.includes(group)) {
            selectedRate = plan.rates[group];
            break;
          }
        }

        if (selectedRate) {
          clonedPlan.rates = selectedRate;
        }
        return clonedPlan;
      });
    }
    return [];
    /*
    Adding resetTableContext as a dependency will cause a nasty infinite loop
    that is very hard to debug
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans, benefitClasses]);

  const getRecommendedPlanName = (plans: any) => {
    if (plans.length === 0) return '';

    const recommendedPlans = plans.filter((plan: any) => plan?.rank === 1);
    const names = recommendedPlans
      .map((plan: any) => plan.name)
      .filter(Boolean);

    return names.join(', ');
  };

  const checkPlanTypesNTier = () => {
    let isNTierPlan = false;
    transformedPlans.forEach((plan: any) => {
      const { rates } = plan || {};
      if (rates) {
        const { type } = rates;
        if (type === 'N_TIER') {
          isNTierPlan = true;
        }
      }
    });
    return isNTierPlan;
  };

  const getRecommendedPlanFrequency = (plans: any) => {
    const recommendedPlan =
      plans.length > 0
        ? plans.find((plan: any) => (plan ? plan.rank === 1 : false))
        : {};
    const { annualPremiumConfig = {} } = recommendedPlan || {};
    const { premiumFrequency = '' } = annualPremiumConfig || {};
    return premiumFrequency;
  };

  // Internationalized header text mapping
  const getInternationalizedHeaderTexts = () => {
    const coverage = {
      '0': getText(PR_COVERAGE_JUST_YOU, language),
      '1': getText(PR_COVERAGE_YOU_PARTNER, language),
      '2': getText(PR_COVERAGE_YOU_CHILDREN, language),
      '3': getText(PR_COVERAGE_YOU_FAMILY, language),
    };

    // Doctor visits with granular underline styling
    const doctorVisits = {
      '0': (
        <>
          <u>{getText(PR_NOT_UNDERLINED, language)}</u>{' '}
          {getText(PR_VISITING_THE_DOCTOR, language)}
        </>
      ),
      '1': (
        <>
          {getText(PR_VISITING_THE_DOCTOR, language).replace(',', '')}{' '}
          <u>{getText(PR_RARELY, language)}</u>,
        </>
      ),
      '2': (
        <>
          {getText(PR_VISITING_THE_DOCTOR, language).replace(',', '')}{' '}
          <u>{getText(PR_OCCASIONALLY, language)}</u>,
        </>
      ),
      '3': (
        <>
          {getText(PR_VISITING_THE_DOCTOR, language).replace(',', '')}{' '}
          <u>{getText(PR_OFTEN, language)}</u>,
        </>
      ),
      '4': (
        <>
          {getText(PR_VISITING_THE_DOCTOR, language).replace(',', '')}{' '}
          <u>{getText(PR_FREQUENTLY, language)}</u>,
        </>
      ),
    };

    // Specialist visits with granular underline styling
    const specialistVisits = {
      '0': (
        <>
          <u>{getText(PR_NOT_UNDERLINED_SPECIALIST, language)}</u>{' '}
          {getText(PR_VISITING_A_SPECIALIST, language)}
        </>
      ),
      '1': (
        <>
          {getText(PR_VISITING_A_SPECIALIST, language).replace(',', '')}{' '}
          <u>{getText(PR_RARELY, language)}</u>,
        </>
      ),
      '2': (
        <>
          {getText(PR_VISITING_A_SPECIALIST, language).replace(',', '')}{' '}
          <u>{getText(PR_OCCASIONALLY, language)}</u>,
        </>
      ),
      '3': (
        <>
          {getText(PR_VISITING_A_SPECIALIST, language).replace(',', '')}{' '}
          <u>{getText(PR_OFTEN, language)}</u>,
        </>
      ),
      '4': (
        <>
          {getText(PR_VISITING_A_SPECIALIST, language).replace(',', '')}{' '}
          <u>{getText(PR_FREQUENTLY, language)}</u>,
        </>
      ),
    };

    // Procedures with granular underline styling
    const procedures = {
      '0': (
        <>
          <u>{getText(PR_NOT_UNDERLINED_PROCEDURES, language)}</u>{' '}
          {getText(PR_PLANNING_A, language)}{' '}
          <u>{getText(PR_PREGNANCY_OR_PROCEDURES, language)}</u>,
        </>
      ),
      '1': getText(PR_PLANNING_PREGNANCY_PROCEDURES, language),
    };

    // Networks with granular underline styling
    const networks = {
      '0': (
        <>
          {getText(PR_USING_A, language)}{' '}
          <u>{getText(PR_NARROW_NETWORK, language)}</u>,
        </>
      ),
      '1': (
        <>
          {getText(PR_USING_A, language)}{' '}
          <u>{getText(PR_WIDE_NETWORK, language)}</u>,
        </>
      ),
      '2': (
        <>
          {getText(PR_USING_A, language)} <u>{getText(PR_KAISER, language)}</u>,
        </>
      ),
    };

    const prescriptions = {
      '0': getText(PR_NOT_TAKING_PRESCRIPTIONS, language),
      '1':
        getText(PR_USING_COST_DRUGS, language) +
        ' ' +
        getText(PR_AND_USING, language),
    };

    return {
      coverage,
      doctorVisits,
      specialistVisits,
      procedures,
      networks,
      prescriptions,
    };
  };

  const getHeaderText = () => {
    const coveringType = getCurrentAnswerByQuestionType(
      questions,
      PLAN_RECOMMENDER_QUESTIONS.COVERAGE
    ) || [0];

    const isProceduresNone = (
      getCurrentAnswerByQuestionType(
        questions,
        PLAN_RECOMMENDER_QUESTIONS.PROCEDURES
      ) || []
    ).includes('NONE');

    const doctorVisits = getCurrentAnswerByQuestionType(
      questions,
      PLAN_RECOMMENDER_QUESTIONS.DOCTOR_VISITS
    );

    const specialistVisits = getCurrentAnswerByQuestionType(
      questions,
      PLAN_RECOMMENDER_QUESTIONS.SPECIALIST_VISITS
    );

    const doctorPreferenceArray = getCurrentAnswerByQuestionType(
      questions,
      PLAN_RECOMMENDER_QUESTIONS.DOCTOR_PREFERENCE
    );

    const doctorPreference = doctorPreferenceArray
      ? doctorPreferenceArray[0]
      : undefined;

    const prescriptions = getCurrentAnswerByQuestionType(
      questions,
      PLAN_RECOMMENDER_QUESTIONS.PRESCRIPTIONS
    ) || [0, 0, 0, 0];

    const drugsType = ['low', 'medium', 'high', 'specialty'].filter(
      (drug, index) => {
        return prescriptions[index] > 0;
      }
    );

    const headerTexts = getInternationalizedHeaderTexts();

    const textSegments = [
      <React.Fragment key={1}>
        {getText(PR_BASED_ON_COVERING_BENEFITS, language)}{' '}
      </React.Fragment>,
      <React.Fragment key={2}>
        <u>{(headerTexts.coverage as any)[coveringType]}</u>{' '}
      </React.Fragment>,
      <React.Fragment key={3}>
        {(headerTexts.procedures as any)[isProceduresNone ? '0' : '1']}{' '}
      </React.Fragment>,
      <React.Fragment key={4}>
        {(headerTexts.doctorVisits as any)[doctorVisits]}{' '}
      </React.Fragment>,
      <React.Fragment key={5}>
        {(headerTexts.specialistVisits as any)[specialistVisits]}{' '}
      </React.Fragment>,
      <React.Fragment key={6}>
        {(headerTexts.networks as any)[doctorPreference]}{' '}
      </React.Fragment>,
      <React.Fragment key={7}>
        {drugsType !== undefined && drugsType.length === 0 ? (
          <>
            {getText(PR_AND_USING, language)}{' '}
            <u>{getText(PR_NOT_TAKING_PRESCRIPTIONS, language)}</u>{' '}
          </>
        ) : (
          <>
            {getText(PR_AND_USING, language)}{' '}
            <u>
              {drugsType !== undefined &&
                drugsType.join(', ').replace(/, ([^,]*)$/, ' & $1')}{' '}
              {getText(PR_USING_COST_DRUGS, language)}
            </u>
            .
          </>
        )}
      </React.Fragment>,
    ];

    return textSegments;
  };
  const {
    onEdit,
    downloadResultPdf,
    answersInProgress,
    resultInProgress,
    toggleMorePlansInfo,
  } = props;

  const recommendedPlanName = getRecommendedPlanName(transformedPlans);

  const recommendedPlanFrequency =
    getRecommendedPlanFrequency(transformedPlans);
  const classes = [
    `${styles.planRecommenderPlansWrapper} planRecommenderPlansWrapper`,
  ];
  if (transformedPlans.length === 0) {
    classes.push(styles.planRecommenderPlansWrapperEmpty);
  }
  const isNTierPlan = checkPlanTypesNTier();
  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} />;

  const getMaxRxCostCount = (plans: any) => {
    let max = 0;
    plans.forEach((plan: any) => {
      const { rxCosts } = plan;
      const tierCount = Object.keys(rxCosts?.inNetwork).length;

      if (tierCount > max) {
        max = tierCount;
      }
    });
    return max;
  };

  const getRxTiers = (divCount: number) => {
    const rxColumns = [];
    for (let i = 1; i <= divCount; i++) {
      rxColumns.push(
        <div
          style={{ height: getMaxHeight('rxCostTier' + i) }}
          className={styles.titleField}
        >
          {getText(PR_RETAIL_TIER, language)} {i}
        </div>
      );
    }
    return <>{rxColumns}</>;
  };

  const rxTierCount = getMaxRxCostCount(transformedPlans);

  const { height: planNameHeight } = useTableHeightSync('planName');

  return (
    <div className={classes.join(' ')}>
      {!props.isEditDrawerOpen && (
        <div
          className={`${styles.planRecommenderLoaderOverlay} ${
            answersInProgress || resultInProgress ? styles.show : styles.hidden
          }`}
        >
          <Spin indicator={antIcon} className={styles.loader} />
        </div>
      )}

      <div className={styles.titleWrapper}>
        <div className={styles.title}>
          {recommendedPlanName &&
            !answersInProgress &&
            !resultInProgress &&
            `${getText(PR_WE_RECOMMEND, language)} ${recommendedPlanName} ${
              isMultipleRecommendations
                ? getText(PR_PLANS, language)
                : getText(PR_PLAN, language)
            } ${getText(PR_FOR_YOU, language)}`}
          {!recommendedPlanName &&
            !answersInProgress &&
            !resultInProgress &&
            getText(PR_NO_RECOMMENDATIONS, language)}
        </div>
        <div className={styles.subTitle}>
          {getHeaderText()}
          <span
            className={`${styles.ml_1} ${styles.editLink}`}
            onClick={onEdit}
          >
            {getText(PR_EDIT_RESPONSES, language)}
          </span>
        </div>
      </div>
      <div className={styles.plansWrapper}>
        <div
          className={styles.plansTitleWrapper}
          style={{ paddingTop: planNameHeight! + 155 }}
        >
          {!isNTierPlan && (
            <div
              className={`${styles.titleField} ${styles.monthlyPremium}`}
            >{`${getFrequencyName(recommendedPlanFrequency)} ${getText(
              PR_PREMIUM,
              language
            )}`}</div>
          )}
          <div
            style={{ height: getMaxHeight('planNetworkName') }}
            className={styles.titleField}
          >
            {getText(PR_PLAN_NETWORK, language)}
          </div>
          <div
            style={{ height: getMaxHeight('deductibleCost') }}
            className={styles.titleField}
          >
            {getText(PR_DEDUCTIBLE, language)}
          </div>
          <div
            style={{ height: getMaxHeight('outOfPocketMax') }}
            className={styles.titleField}
          >
            {getText(PR_OUT_OF_POCKET_MAX, language)}
          </div>
          <div
            style={{ height: getMaxHeight('officeVisit') }}
            className={styles.titleField}
          >
            {getText(PR_OFFICE_VISIT, language)}
          </div>
          <div
            style={{ height: getMaxHeight('specialistVisit') }}
            className={styles.titleField}
          >
            {getText(PR_SPECIALIST_VISIT, language)}
          </div>
          <div
            style={{ height: getMaxHeight('hospitalInpatient') }}
            className={styles.titleField}
          >
            {getText(HOSPITAL_INPATIENT, language)}
          </div>
          <div
            style={{ height: getMaxHeight('hospitalOutPatient') }}
            className={styles.titleField}
          >
            {getText(HOSPITAL_OUTPATIENT, language)}
          </div>
          {getRxTiers(rxTierCount)}
          <div
            ref={hsaRef}
            style={{ height: hsaHeight }}
            className={`${styles.titleField} ${styles.hsa}`}
          >
            {getText(PR_HSA_CONTRIBUTION_HRA_ALLOWANCE, language)}
          </div>
          <div
            style={{ height: getMaxHeight('annualCost') }}
            className={`${styles.titleField} ${styles.annualCost}`}
          >
            {getText(PR_EST_ANNUAL_COST, language)}
          </div>
          <div className={`${styles.titleField} ${styles.annualCostBreakdown}`}>
            {getText(PR_EST_ANNUAL_COST_BREAKDOWN, language)}
          </div>
        </div>
        <div className={styles.plansDetailsWrapper}>
          {!answersInProgress && !resultInProgress && (
            <div className={styles.planEmptyMsgWrapper}>
              <div className={styles.planEmptyMsg}>
                {getText(PR_UNABLE_TO_RECOMMEND, language)}
              </div>
              <button
                className={`${styles.nextButton} nextButton`}
                onClick={onEdit}
              >
                {getText(PR_EDIT_REFINE_RESPONSES, language)}
              </button>
            </div>
          )}

          <div className={styles.planCardsContainer}>
            <div className={`${styles.plan} ${styles.planEmpty}`}></div>
            <div className={`${styles.plan} ${styles.planEmpty}`}></div>
            <div className={`${styles.plan} ${styles.planEmpty}`}></div>
            {transformedPlans.map((plan: any, index: number) => (
              <PlanRecommenderPlan
                key={index}
                index={index}
                plan={plan}
                rank={plan.rank}
                activePlan={activePlan}
                onClick={() => {
                  setActivePlan(index);
                }}
                isNTierPlan={isNTierPlan}
                dbgPlanFrequency={recommendedPlanFrequency}
                maxRxTiers={rxTierCount}
              />
            ))}
          </div>
          <div className={styles.breakdownWrapper}>
            <AnnualCostBreakdown activePlan={transformedPlans[activePlan]} />
          </div>
        </div>
        {transformedPlans.length > 3 && (
          <div className={styles.plansMoreWrapper}>
            <span
              className={styles.morePlansPointer}
              onClick={toggleMorePlansInfo}
            >
              +{transformedPlans.length - 3} {getText(PR_MORE_PLANS, language)}
            </span>
          </div>
        )}
      </div>
      {/* TODO - Removed temporary */}
      {/* {benAdminUrl && (
        <div className={styles.footerLink}>
          <button
            className={`${styles.nextButton} nextButton`}
            onClick={() => {
              window.open(benAdminUrl, '_blank');
            }}
          >
            Go to my BenAdmin
          </button>
        </div>
      )} */}
      <div className={styles.footerLink}>
        <button
          className={`${styles.nextButton} ${styles.downloadResultButton} nextButton`}
          onClick={downloadResultPdf}
        >
          {getText(PR_DOWNLOAD_RESULTS, language)}
        </button>
      </div>
      {/* TODO - Removed temporary */}
      {/* {plans.length > 0 && isbenefitsConsultationEnabled && (
        <div className={`${styles.footerLink} pt-2`}>
          <div className={styles.scheduleMeetingLinkBtn} onClick={openCalendly}>
            Questions? Schedule a Benefits Consultation
          </div>
        </div>
      )} */}
      <PlanRecommenderDisclaimer />
    </div>
  );
};

export default PlanRecommenderPlans;
